import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  Alert,
  Chip,
  Divider,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Repeat as RepeatIcon,
  Schedule as ScheduleIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';

const RecurrenceDeleteModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  taskTitle,
  recurrenceInfo,
  allowStopRecurrence = true,
  allowDeleteFuture = false,
  initialStopRecurrence = true,
  initialDeleteFuture = false
}) => {
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteMode, setDeleteMode] = useState('single');

  useEffect(() => {
    if (open) {
      setConfirmChecked(false);
      setLoading(false);
      if (initialDeleteFuture && allowDeleteFuture) {
        setDeleteMode('future');
      } else if (initialStopRecurrence && allowStopRecurrence) {
        setDeleteMode(allowDeleteFuture ? 'future' : 'single');
      } else {
        setDeleteMode('single');
      }
    }
  }, [open, initialStopRecurrence, initialDeleteFuture, allowDeleteFuture, allowStopRecurrence]);

  const handleClose = () => {
    setConfirmChecked(false);
    setLoading(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!confirmChecked) return;
    
    setLoading(true);
    try {
      await onConfirm({ mode: deleteMode });
      handleClose();
    } catch (error) {
      setLoading(false);
      console.error('Erro ao deletar tarefa:', error);
    }
  };

  const getRecurrenceTypeLabel = (type) => {
    const types = {
      'daily': 'Diária',
      'weekly': 'Semanal', 
      'monthly': 'Mensal',
      'yearly': 'Anual'
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  const getRadioColor = (mode) => {
    if (mode === 'all') return 'error';
    if (mode === 'future') return 'secondary';
    return 'primary';
  };

  const getOptionHighlight = (mode) => {
    if (mode === 'all') return '#d32f2f';
    if (mode === 'future') return '#0288d1';
    return '#1976d2';
  };

  const getConsequences = (mode) => {
    if (mode === 'future') {
      return [
        '✓ Esta tarefa e todas as ocorrências futuras pendentes serão removidas.',
        '✓ A recorrência será interrompida imediatamente.'
      ];
    }

    if (mode === 'all') {
      return [
        '✓ Toda a série (tarefas passadas, atual e futuras) será apagada.',
        '✓ A configuração de recorrência será eliminada deste fluxo.'
      ];
    }

    return [
      '✓ Apenas esta tarefa será deletada.',
      '✓ A recorrência permanece ativa e outras ocorrências continuam disponíveis.'
    ];
  };

  const renderOptionCard = (value, title, description) => {
    const accent = getOptionHighlight(value);
    const isSelected = deleteMode === value;

    return (
      <Box
        key={value}
        sx={{
          border: isSelected ? `2px solid ${accent}` : '1px solid #e0e0e0',
          borderRadius: 2,
          mb: 1.5,
          px: 2,
          py: 1.5,
          backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : '#ffffff',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: accent,
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          }
        }}
        onClick={() => setDeleteMode(value)}
      >
        <FormControlLabel
          value={value}
          control={<Radio color={getRadioColor(value)} />}
          label={
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {description}
              </Typography>
            </Box>
          }
          sx={{ width: '100%', margin: 0 }}
        />
      </Box>
    );
  };

  if (!recurrenceInfo) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        backgroundColor: '#fff3cd',
        color: '#856404',
        borderRadius: '12px 12px 0 0',
        margin: '-8px -8px 0 -8px'
      }}>
        <WarningIcon sx={{ fontSize: 32, color: '#f57c00' }} />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            ⚠️ ATENÇÃO - Tarefa com Recorrência
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Esta ação afetará o sistema de recorrência
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        {/* Informações da Tarefa */}
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<RepeatIcon />}
        >
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            A tarefa "{taskTitle}" possui recorrência ativa
          </Typography>
          <Typography variant="body2">
            Defina abaixo como deseja tratar as próximas ocorrências antes de confirmar a exclusão.
          </Typography>
        </Alert>

        {/* Situação Atual */}
        <Box sx={{ 
          backgroundColor: '#f8f9fa', 
          padding: 2, 
          borderRadius: 2, 
          mb: 3,
          border: '1px solid #e9ecef'
        }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            📊 Situação Atual
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip 
              icon={<TaskIcon />}
              label={`${recurrenceInfo.pendingCount} tarefas pendentes`}
              color="info"
              variant="outlined"
            />
            <Chip 
              icon={<RepeatIcon />}
              label={`${getRecurrenceTypeLabel(recurrenceInfo.recurrenceType)} (a cada ${recurrenceInfo.recurrenceInterval})`}
              color="secondary"
              variant="outlined"
            />
          </Box>

          {recurrenceInfo.nextDueDate && (
            <Typography variant="body2" color="textSecondary">
              🗓️ Próxima tarefa: {formatDate(recurrenceInfo.nextDueDate)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            ⚙️ Escolha a ação desejada
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={deleteMode} onChange={(event) => setDeleteMode(event.target.value)}>
              {renderOptionCard('single', 'Excluir apenas esta tarefa', 'Mantém a recorrência ativa. As próximas ocorrências continuam disponíveis para o time.')}
              {allowStopRecurrence && allowDeleteFuture && renderOptionCard('future', 'Excluir esta e futuras pendentes', 'Interrompe a recorrência imediatamente e remove as próximas ocorrências ainda não concluídas.')}
              {allowStopRecurrence && renderOptionCard('all', 'Excluir toda a série', 'Remove esta tarefa, todas as ocorrências passadas e futuras, limpando a configuração de recorrência.')}
            </RadioGroup>
          </FormControl>
        </Box>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            🔁 Novas ocorrências só são geradas quando a tarefa recorrente atual é marcada como concluída. Enquanto estiver pendente, nenhuma nova tarefa será criada automaticamente.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="error">
            🚨 Resultado da opção escolhida
          </Typography>

          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            {getConsequences(deleteMode).map((text) => (
              <li key={text}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {text}
                </Typography>
              </li>
            ))}
            <li>
              <Typography variant="body2" color="error">
                ❌ Esta ação não pode ser desfeita.
              </Typography>
            </li>
          </Box>
        </Box>

        {/* Checkbox de Confirmação Obrigatório */}
        <Box sx={{ 
          backgroundColor: '#ffe6e6', 
          padding: 2, 
          borderRadius: 2,
          border: '2px solid #ffcdd2'
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                color="error"
                sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
              />
            }
            label={
              <Typography variant="body1" fontWeight="bold">
                🔒 Eu li e compreendo as consequências. Quero prosseguir com a opção selecionada.
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px 24px' }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: 25,
            padding: '10px 24px',
            fontWeight: 600,
            minWidth: 120
          }}
        >
          Cancelar
        </Button>
        
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!confirmChecked || loading}
          startIcon={loading ? null : <DeleteIcon />}
          sx={{
            borderRadius: 25,
            padding: '10px 24px',
            fontWeight: 600,
            minWidth: 220,
            opacity: !confirmChecked ? 0.5 : 1,
            '&:disabled': {
              backgroundColor: '#ffcdd2',
              color: '#c62828'
            }
          }}
        >
          {loading ? 'Deletando...' : '⚠️ Confirmar exclusão'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurrenceDeleteModal;
